import { get, post, put, patch, del } from './client';

/** 用户状态值域（Party.status），与后端 users/dto/user.dto.ts 的 USER_STATUSES 一致 */
export const USER_STATUSES = ['激活', '停用', '禁用'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/** 用户基础信息（管理员用户列表条目） */
export interface User {
  id: string;
  username: string;
  fullName?: string | null;
  phone?: string | null;
  email?: string | null;
  avatar?: string | null;
  role: string;
  /** 后端算好的管理员标记（role 为 'admin' 或历史值 '管理员' 时都为 true）；客户端不得自行比较 role 字符串 */
  isAdmin?: boolean;
  status: string;
  /** 停用/禁用原因；恢复为「激活」时后端会清空 */
  stopReason?: string | null;
  lastLogin?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** GET /users 分页结果 */
export interface UserListResult {
  items: User[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

/** 用户列表查询参数：页码 / 每页条数 / 关键字（姓名、用户名、手机、邮箱） */
export interface GetUsersParams {
  page?: number;
  pageSize?: number;
  keyword?: string;
}

/** 获取用户列表（仅管理员；非管理员返回 success:false） */
export async function getUsers(params?: GetUsersParams) {
  return get<UserListResult>('/users', { params: { ...params } });
}

/**
 * 管理员创建用户。
 * 后端 POST /users：username / fullName / phone 必填，email / password / role 可选；
 * password 留空表示该账号尚无密码，由用户走激活或忘记密码流程自行设置。
 */
export async function createUser(data: Record<string, unknown>) {
  return post<User>('/users', data);
}

/** 删除用户（仅管理员，不能删除自己） */
export async function deleteUser(id: string) {
  return del<void>(`/users/${id}`);
}

/**
 * 更新用户状态。
 * 「禁用」属于管理员管制，后端强制要求填写原因；「激活」「停用」无需原因，
 * 且从禁用恢复为激活时后端会一并清空历史原因。
 */
export async function updateUserStatus(id: string, status: UserStatus, stopReason?: string) {
  return patch<User>(`/users/${id}/status`, { status, stopReason });
}

export async function changePassword(data: ChangePasswordData) {
  return put<void>('/users/me/password', data);
}

/**
 * 注销前预览（GET /users/me/cancellation/preview）。
 * 与后端 account-cancellation.service 的 CancellationPreview 同形：mode 决定页面文案，
 * reasons 是「为什么你的档案会留下来」，四端都按整句直接显示，不再自行拼句子。
 */
export interface CancellationPreview {
  mode: 'erase' | 'deactivate';
  reasons: string[];
  stats: {
    orphanOrders: number;
    interactiveOrders: number;
    orphanCustomers: number;
    keptCustomers: number;
    voucherFiles: number;
  };
  phone: string;
}

export async function getCancellationPreview() {
  return get<CancellationPreview>('/users/me/cancellation/preview');
}

/** 发注销验证码（手机号未绑定时后端回业务失败，按 toast 显示即可） */
export async function sendCancellationCode() {
  return post<{ success: boolean; message: string }>('/users/me/cancellation/send-code');
}

/**
 * 执行注销（POST /users/me/cancellation）。
 * 成功即代表本账号的令牌已被吊销，调用方必须立刻清本地会话并跳登录页，不得再发请求。
 */
export async function cancelAccount(data: { code: string; password?: string }) {
  return post<CancellationPreview>('/users/me/cancellation', data);
}

/**
 * 在用登录会话（设备）。设备名由服务端从 User-Agent 归一——`refresh_session` 只有
 * ip / user_agent 两列，不加列就不许客户端自己猜平台。
 * loginAt / expiresAt 是 UTC 瞬间下发的字符串，展示前必须转本地日历日，直接截会整体差一天。
 */
export interface LoginSession {
  id: string;
  deviceLabel: string;
  ip: string | null;
  loginAt: string | null;
  expiresAt: string;
  current: boolean;
}

/**
 * GET /auth/sessions。路径前缀不能换：refreshToken 只在 `/auth/*` 随 HttpOnly cookie 送出，
 * 挂到别的前缀就取不到本机令牌，`current` 恒为 false（本机那台设备认不出来）。
 */
export async function getLoginSessions() {
  const res = await get<{ sessions?: LoginSession[] }>('/auth/sessions');
  return Array.isArray(res?.sessions) ? res.sessions : [];
}

/**
 * POST /auth/sessions/:id/revoke。
 * `self: true` 表示刚下线的是当前这台设备——后端已顺带清了 cookie，
 * 调用方必须立刻走正常登出并回登录页，不能只刷新列表。
 */
export async function revokeLoginSession(id: string) {
  return post<{ self?: boolean }>(`/auth/sessions/${id}/revoke`);
}
